<template>
  <div class="map-routing-overlay">
    <SettingButtonsComponent v-if="!hideControls" :isRoutingActive="false" />
    <div ref="mapRouting" class="map-container"></div>
    <div class="map-bottom-controls">
      <div ref="mapNavControl" class="nav-control-wrapper"></div>
      <div class="control-button" :class="{'nav-arrow--active': isNavigating}" @click="toggleNavigation">
        <span class="material-icons-round">
          {{ isNavigating ? "stop" : "navigation" }}
        </span>
      </div>
      <div class="control-button" @click="toggle2DView">
        {{ map && map.getPitch() > 0 ? "2D" : "3D" }}
      </div>
    </div>
    <div class="routing-ui" id="lhs_wrapperr">
      <div class="sidebar">
        <div v-if="!instructionsVisible">
          <div class="header">
            <div class="title">
              <img
                src="@/assets/icons/delivery_24x24.png"
                alt="Routing Icon"
                style="height: 24px; width: 24px; margin-right: 8px"
              />
              {{ $t("routing.title") }}
            </div>
            <!-- <button @click="closeRouting" class="close-btn">✕</button> -->
            <button @click.stop="closeRouting" class="close-btn">✕</button>
          </div>
          <div class="transport-header-row">
            <div class="transport-modes">
              <button :class="['transport-btn', {active: mode === 'car'}]" @click="setMode('car')">
                <span class="material-icons-round">directions_car</span>
              </button>
              <button :class="['transport-btn', {active: mode === 'bike'}]" @click="setMode('bike')">
                <span class="material-icons-round">directions_bike</span>
              </button>
              <button :class="['transport-btn', {active: mode === 'walk'}]" @click="setMode('walk')">
                <span class="material-icons-round">directions_walk</span>
              </button>
            </div>
            <div v-if="distanceText && durationText" class="summary-box">
              <div class="summary-time">🕒 {{ durationText }}</div>
              <div class="summary-distance">📌 {{ distanceText }}</div>
            </div>
          </div>
          <div class="collapse-toggle-row" v-if="startInput || endInput">
            <button class="collapse-toggle-btn" @click="collapsed = !collapsed">
              <span class="toggle-text">
                {{ collapsed ? $t("routing.showMore") : $t("routing.showLess") }}
              </span>
              <span class="material-icons-round toggle-icon">
                {{ collapsed ? "expand_more" : "expand_less" }}
              </span>
            </button>
          </div>

          <div v-show="!collapsed" class="points">
            <div class="point start">
              <img src="@/assets/icons/green_pin.png" alt="Start" height="45" />
              <div class="input-wrapper">
                <input
                  v-model="startInput"
                  @input="onInput('start', startInput)"
                  @focus="
                    selectingPoint = 'start';
                    showStartLocationBtn = true;
                  "
                  @blur="setTimeout(() => (showStartLocationBtn = false), 200)"
                  :placeholder="$t('routing.startPlaceholder')"
                  class="styled-input"
                />
                <ul v-if="startSuggestions.length" class="suggestions-list">
                  <li v-for="s in startSuggestions" :key="s.place_id" @click="selectSuggestion('start', s)">
                    {{ s.description }}
                  </li>
                </ul>
                <div v-if="showStartLocationBtn" class="my-location-option" @click="useCurrentLocation('start')">
                  <img src="@/assets/icons/icons8-my-location-48.png" alt="My Location" height="18" />
                  {{ $t("routing.useMyLocation") }}
                </div>
              </div>
            </div>
            <button class="swap-fab" @click="swapPoints" title="Swap Start ↔ End">
              <span class="material-icons-round">swap_vert</span>
            </button>
            <div class="point end">
              <img src="@/assets/icons/red_pin.png" alt="End" height="45" />
              <div class="input-wrapper">
                <input
                  v-model="endInput"
                  @input="onInput('end', endInput)"
                  @focus="
                    selectingPoint = 'end';
                    showEndLocationBtn = true;
                  "
                  @blur="setTimeout(() => (showEndLocationBtn = false), 200)"
                  :placeholder="$t('routing.endPlaceholder')"
                  class="styled-input"
                />
                <ul v-if="endSuggestions.length" class="suggestions-list">
                  <li v-for="s in endSuggestions" :key="s.place_id" @click="selectSuggestion('end', s)">
                    {{ s.description }}
                  </li>
                </ul>
                <div v-if="showEndLocationBtn" class="my-location-option" @click="useCurrentLocation('end')">
                  <img src="@/assets/icons/icons8-my-location-48.png" alt="My Location" height="18" />
                  {{ $t("routing.useMyLocation") }}
                </div>
              </div>
            </div>
          </div>

          <div v-show="!collapsed" class="instructions-btn">
            <button @click="toggleInstructions">
              <span class="text">{{ $t("routing.instructionsTitle") }}</span>
            </button>
          </div>
        </div>
        <div v-show="!collapsed" v-if="instructionsVisible" class="instructions-slide">
          <div class="header-instructions">
            <div class="title">
              <img
                src="@/assets/icons/ways.png"
                alt="Instructions Icon"
                style="height: 24px; width: 24px; margin-right: 8px"
              />
              {{ $t("routing.instructionsTitle") }}
            </div>
            <button @click="toggleInstructions" class="close-btn">✕</button>
          </div>

          <div class="mode-summary">
            <div class="start-end-line">
              <div class="pin-wrapper">
                <img src="@/assets/icons/green_pin.png" class="pin-icon" alt="Start" />
              </div>
              <div class="line-container">
                <div class="dotted-line"></div>
                <div class="transport-bubble">
                  <span class="material-icons-round transport-icon">{{ modeMaterialIcon }}</span>
                  <span class="duration-text" v-if="durationText">{{ durationText }}</span>
                </div>
              </div>
              <div class="pin-wrapper">
                <img src="@/assets/icons/red_pin.png" class="pin-icon" alt="End" />
              </div>
            </div>
          </div>
          <div v-if="instructionsVisible" class="instructions-panel-v3">
            <div class="address-row">
              <div class="address-icon">
                <img src="@/assets/icons/green_pin.png" alt="Start" height="45" />
              </div>
              <div class="address-text">
                <strong>{{ startInput }}</strong>
              </div>
            </div>

            <ul class="instruction-list">
              <li
                v-for="(instr, index) in instructionList"
                :key="index"
                @click="highlightInstructionPoint(instr)"
                style="cursor: pointer"
              >
                <div class="step">
                  <span class="material-icons-round instruction-icon">
                    {{ getIconForInstruction(instr.text).icon }}
                  </span>
                  <span class="instruction-text">
                    {{ getIconForInstruction(instr.text).text }}
                  </span>
                </div>
                <div class="distance">{{ instr.distance }} m</div>
              </li>
            </ul>
            <div class="address-row end">
              <div class="address-icon red">
                <img src="@/assets/icons/red_pin.png" alt="End" height="45" />
              </div>
              <div class="address-text">
                <strong>{{ endInput }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- <div ref="mapRouting" class="map-container"></div> -->
    <div v-if="loading" class="loading-spinner">{{ $t("routing.loading") }}</div>
  </div>
</template>

<script>
import maplibregl from "maplibre-gl";
// import polyline from "polyline";
import SettingButtonsComponent from "@/components/button_controls/SettingButtons.vue";

/* global google */

export default {
  name: "MapRouting",
  emits: ["close"],
  components: {
    SettingButtonsComponent,
  },

  data() {
    return {
      map: null,
      startCoords: null,
      endCoords: null,
      selectingPoint: "start",
      instructionMarkers: [],
      endMarker: null,
      loading: false,
      lastDrawnStart: null,
      lastDrawnEnd: null,
      startInput: "",
      endInput: "",
      startSuggestions: [],
      endSuggestions: [],
      mode: "car",
      distanceText: null,
      durationText: null,
      autocompleteService: null,
      geocoder: null,
      lastDrawnMode: null,
      showStartLocationBtn: true,
      showEndLocationBtn: true,
      instructionsVisible: false,
      instructionList: [],
      highlightMarker: null,
      startMarker: null,
      startDotMarker: null,
      endDotMarker: null,
      collapsed: false,
      is3DView: true,
      hideControls: true,
      viaPoints: [],
      viaMarkers: [],
      lastDrawnVia: null,
      draggingRoute: false,
      ghostViaMarker: null,

      isNavigating: false,
      geoWatchId: null,
      userMarker: null,
      userHeading: null,
      routeCoords: null,
      routeArrowMarker: null,
      routeArrowAnimId: null,
      routeSegmentLengths: [],
      routeTotalLength: 0,
      routeArrowDistance: 0,
    };
  },

  computed: {
    modeMaterialIcon() {
      if (this.mode === "car") return "directions_car";
      if (this.mode === "bike") return "directions_bike";
      if (this.mode === "walk") return "directions_walk";
      return "";
    },
  },

  props: {
    initialLat: Number,
    initialLng: Number,
    initialZoom: {
      type: Number,
      default: 13,
    },
    start: Object,
    end: Object,
  },
  watch: {
    start: {
      deep: true,
      immediate: true,
      handler(v) {
        const norm = (s) =>
          Array.isArray(s)
            ? [Number(s[0]), Number(s[1])]
            : s && typeof s === "object" && typeof s.lat === "number" && typeof s.lng === "number"
            ? [s.lat, s.lng]
            : null;

        const sc = norm(v);
        if (!sc) return;

        this.startCoords = sc;
        this.reverseGeocode("start", sc);
        this.selectingPoint = "end";

        if (this.map && this.map.isStyleLoaded()) this.drawStartMarker();
        else this.map?.once("load", () => this.drawStartMarker());

        this.map?.easeTo({center: [sc[1], sc[0]], zoom: this.initialZoom || 16});
      },
    },

    end: {
      deep: true,
      immediate: true,
      handler(v) {
        const norm = (s) =>
          Array.isArray(s)
            ? [Number(s[0]), Number(s[1])]
            : s && typeof s === "object" && typeof s.lat === "number" && typeof s.lng === "number"
            ? [s.lat, s.lng]
            : null;

        const ec = norm(v);
        if (!ec) return;

        this.endCoords = ec;
        this.reverseGeocode("end", ec);
        this.selectingPoint = this.startCoords ? null : "start";

        if (this.map && this.map.isStyleLoaded()) this.drawStopMarker();
        else this.map?.once("load", () => this.drawStopMarker());
      },
    },
  },
  // watch: {
  //   start: {
  //     deep: true,
  //     immediate: true,
  //     handler(v) {
  //       const norm = (s) =>
  //         Array.isArray(s) ? [Number(s[0]), Number(s[1])] :
  //         (s && typeof s === 'object' && typeof s.lat === 'number' && typeof s.lng === 'number')
  //           ? [s.lat, s.lng] : null;

  //       const sc = norm(v);
  //       if (!sc) return;
  //       // this.startCoords = sc;
  //       // this.reverseGeocode('start', sc);
  //       // this.selectingPoint = 'end';

  //       this.endCoords = sc;
  //       this.reverseGeocode('end', sc);
  //       this.selectingPoint = 'start';

  //       // if (this.map && this.map.isStyleLoaded()) this.drawStartMarker();
  //       // else this.map?.once('load', () => this.drawStartMarker());
  //       if (this.map && this.map.isStyleLoaded()) this.drawStopMarker();
  //       else this.map?.once('load', () => this.drawStopMarker());

  //       this.map?.easeTo({ center: [sc[1], sc[0]], zoom: this.initialZoom || 16 });
  //     }
  //   }
  // },

  mounted() {
    let lat = this.initialLat;
    let lng = this.initialLng;
    let zoom = this.initialZoom;

    if (!lat || !lng) {
      const pos = this.$root.$data.globalRoutingPosition;
      if (pos && pos.lat && pos.lng) {
        lat = pos.lat;
        lng = pos.lng;
        zoom = pos.zoom ?? 15;
        console.log("Coordinates retrieved from globalRoutingPosition:", pos);
      } else {
        console.warn("No valid coordinates available, using fallback 45.75, 21.23.");
        lat = 45.75;
        lng = 21.23;
        zoom = 13;
      }
    }
    this.initMap(lat, lng, zoom);
    // if (this.start && !this.startCoords) {
    //   this.startCoords = [this.start.lat, this.start.lng];
    //   this.reverseGeocode("start", this.startCoords);
    //   this.drawStartMarker();
    // }
    if (this.end && !this.endCoords) {
      this.endCoords = [this.end.lat, this.end.lng];
      this.reverseGeocode("end", this.endCoords);
      this.drawStopMarker();
    }
    if (this.end && !this.endCoords) {
      this.endCoords = [this.end.lat, this.end.lng];
      this.reverseGeocode("end", this.endCoords);
    }

    if (this.startCoords && this.endCoords) {
      this.tryDrawRoute();
    }

    if (window.google && google.maps) {
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.geocoder = new google.maps.Geocoder();
    } else {
      console.warn("Google Maps API is missing.");
    }

    this.$nextTick(() => {
      setTimeout(() => {
        const container = this.$refs.mapRouting;
        const canvas = container?.querySelector("canvas");

        if (this.map) {
          console.log("[MAP][resize] Primary resize triggered");
          this.map.resize();
        }

        if (canvas) {
          console.log("[MAP][resize] Canvas size after delay:", canvas.offsetWidth, canvas.offsetHeight);
          canvas.style.width = "100%";
          canvas.style.height = "100%";
        }

        setTimeout(() => {
          if (this.map) {
            console.log("[MAP][resize] Secondary resize triggered (fallback)");
            this.map.resize();
          }
        }, 1000);
      }, 300);
    });

    this._openRoutingHandler = (e) => this.openFromPOI(e.detail || {});
    window.addEventListener("openRoutingMapMTK", this._openRoutingHandler);
  },

  methods: {
    createNavArrowElement() {
      const el = document.createElement("div");
      el.className = "nav-arrow-marker nav-arrow-marker--pulse";
      el.innerHTML = `
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <!-- săgeată tip „navigation” -->
          <path d="M12 2L19 21L12 17L5 21L12 2Z" fill="white" />
        </svg>
      `;
      return el;
    },

    _getPointOnRoute(targetDist) {
      if (!this.routeCoords || this.routeCoords.length < 2) return null;
      if (targetDist <= 0) return this.routeCoords[0];
      if (targetDist >= this.routeTotalLength) {
        return this.routeCoords[this.routeCoords.length - 1];
      }

      let acc = 0;
      for (let i = 1; i < this.routeCoords.length; i++) {
        const segLen = this.routeSegmentLengths[i - 1];
        const nextAcc = acc + segLen;

        if (nextAcc >= targetDist) {
          const t = (targetDist - acc) / segLen;
          const [lng1, lat1] = this.routeCoords[i - 1];
          const [lng2, lat2] = this.routeCoords[i];
          const lng = lng1 + (lng2 - lng1) * t;
          const lat = lat1 + (lat2 - lat1) * t;
          return [lng, lat];
        }
        acc = nextAcc;
      }

      return this.routeCoords[this.routeCoords.length - 1];
    },

    _distMeters(a, b) {
      const R = 6371000; // m
      const toRad = (x) => (x * Math.PI) / 180;
      const lat1 = toRad(a[1]);
      const lat2 = toRad(b[1]);
      const dLat = toRad(b[1] - a[1]);
      const dLng = toRad(b[0] - a[0]);

      const sinDLat = Math.sin(dLat / 2);
      const sinDLng = Math.sin(dLng / 2);
      const aa = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
      const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
      return R * c;
    },
    startRouteArrow() {
      if (!this.map || !this.routeCoords || this.routeCoords.length < 2) return;

      this.stopRouteArrow();

      if (!this.routeArrowMarker) {
        const el = this.createNavArrowElement();

        this.routeArrowMarker = new maplibregl.Marker({
          element: el,
          anchor: "center",
        })
          .setLngLat(this.routeCoords[0])
          .addTo(this.map);
      }
      this.routeArrowDistance = 0;
      const speed = 1.4;

      let lastTs = null;
      const step = (ts) => {
        if (!lastTs) lastTs = ts;
        const dt = (ts - lastTs) / 1000;
        lastTs = ts;

        this.routeArrowDistance += speed * dt;
        if (this.routeArrowDistance > this.routeTotalLength) {
          this.routeArrowDistance = this.routeTotalLength;
        }

        const pt = this._getPointOnRoute(this.routeArrowDistance);
        if (pt) {
          this.routeArrowMarker.setLngLat(pt);
        }

        if (this.routeArrowDistance < this.routeTotalLength) {
          this.routeArrowAnimId = requestAnimationFrame(step);
        } else {
          this.routeArrowAnimId = null;
        }
      };

      this.routeArrowAnimId = requestAnimationFrame(step);
    },

    stopRouteArrow() {
      if (this.routeArrowAnimId) {
        cancelAnimationFrame(this.routeArrowAnimId);
        this.routeArrowAnimId = null;
      }
      if (this.routeArrowMarker) {
        this.routeArrowMarker.remove();
        this.routeArrowMarker = null;
      }
    },
    toggleNavigation() {
      if (this.isNavigating) {
        // STOP
        this.stopNavigation();
        this.stopRouteArrow();
      } else {
        if (!this.routeCoords || this.routeCoords.length < 2) {
          alert(this.$t("routing.needBothPoints") || "Set start");
          return;
        }

        this.startNavigation();
      }
    },
    async startNavigation() {
      if (!this.startCoords || !this.endCoords) {
        alert(this.$t("routing.needBothPoints") || "Set start.");
        return;
      }
      if (!navigator.geolocation) {
        alert("Geolocation not supported from browser.");
        return;
      }

      let pos;
      try {
        pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            maximumAge: 1000,
            timeout: 10000,
          });
        });
      } catch (err) {
        console.error("Geo error (getCurrentPosition):", err);
        alert("Not read the location: " + err.message);
        return;
      }
      

      const {latitude, longitude} = pos.coords;
      const distToStart = this._distMeters([this.startCoords[1], this.startCoords[0]], [longitude, latitude]);
      const MAX_START_OFFSET = 0.5;

      if (distToStart > MAX_START_OFFSET) {
        alert(this.$t("routing.startMustBeMyLocation") || "use (Use My Location).");
        return;
      }
      if (!this.routeCoords || this.routeCoords.length < 2) {
        await this.fetchRoute();
        if (!this.routeCoords || this.routeCoords.length < 2) {
          alert("Nu am putut calcula ruta.");
          return;
        }
      }
      this.onLocationUpdate(pos, {firstFix: true});
      this.geoWatchId = navigator.geolocation.watchPosition(
        (p) => this.onLocationUpdate(p),
        (err) => console.error("Geo error (watchPosition):", err),
        {
          enableHighAccuracy: true,
          maximumAge: 1000,
          timeout: 10000,
        }
      );

      this.isNavigating = true;
      this.instructionsVisible = true;
      this.collapsed = false;

      if (this.map) {
        this.map.easeTo({
          duration: 300,
        });
      }
    },
    stopNavigation() {
      if (this.geoWatchId !== null) {
        navigator.geolocation.clearWatch(this.geoWatchId);
        this.geoWatchId = null;
      }

      this.isNavigating = false;

      if (this.userMarker) {
        this.userMarker.remove();
        this.userMarker = null;
      }

      if (this.map) {
        this.map.easeTo({pitch: 0, bearing: 0, duration: 400});
      }
    },
    onLocationUpdate(pos, opts = {}) {
      const {latitude, longitude, heading} = pos.coords;
      let lng = longitude;
      let lat = latitude;
      if (this.routeCoords && this.routeCoords.length > 1 && !opts.firstFix) {
        const snapped = this.snapToRoute(lng, lat);
        lng = snapped[0];
        lat = snapped[1];
      }

      const lngLat = [lng, lat];

      if (!this.userMarker) {
        const el = this.createNavArrowElement();
        this.userMarker = new maplibregl.Marker({
          element: el,
          anchor: "center",
        })
          .setLngLat(lngLat)
          .addTo(this.map);
      } else {
        this.userMarker.setLngLat(lngLat);
        this.userMarker.getElement().classList.add("nav-arrow-marker--pulse");
      }

      if (typeof heading === "number" && !isNaN(heading)) {
        this.userHeading = heading;
      }

      if (this.isNavigating && this.map) {
        this.map.easeTo({
          center: lngLat,
          zoom: Math.max(this.map.getZoom(), 16),
          // pitch: Math.max(this.map.getPitch(), 60),
          bearing: this.userHeading ?? this.map.getBearing(),
          duration: opts.firstFix ? 0 : 400,
        });
      }
    },

    snapToRoute(lng, lat) {
      const coords = this.routeCoords;
      if (!coords || coords.length < 2) return [lng, lat];

      const p = this.map.project({lng, lat});
      let best = {d2: Infinity, snapped: [lng, lat]};

      for (let i = 1; i < coords.length; i++) {
        const aLL = {lng: coords[i - 1][0], lat: coords[i - 1][1]};
        const bLL = {lng: coords[i][0], lat: coords[i][1]};
        const a = this.map.project(aLL);
        const b = this.map.project(bLL);

        const abx = b.x - a.x,
          aby = b.y - a.y;
        const apx = p.x - a.x,
          apy = p.y - a.y;
        const ab2 = abx * abx + aby * aby;
        const t = ab2 === 0 ? 0 : Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));

        const qx = a.x + abx * t,
          qy = a.y + aby * t;
        const d2 = (qx - p.x) ** 2 + (qy - p.y) ** 2;

        if (d2 < best.d2) {
          const ll = this.map.unproject({x: qx, y: qy});
          best = {d2, snapped: [ll.lng, ll.lat]};
        }
      }

      // const end = coords[coords.length - 1]; // [lng, lat]
      // const endPx = this.map.project({ lng: end[0], lat: end[1] });
      // const distEnd = Math.hypot(endPx.x - p.x, endPx.y - p.y);
      // if (distEnd <= 12) return [end[0], end[1]];  // 12px prag de snap

      return best.snapped; // [lng, lat]
    },

    swapPoints() {
      if (this.isNavigating) {
        this.stopNavigation();
        this.stopRouteArrow?.();
      }

      this.userHeading = null;
      this.routeCoords = null;
      this.routeSegmentLengths = [];
      this.routeTotalLength = 0;
      this.routeArrowDistance = 0;

      if (this.userMarker) {
        this.userMarker.remove();
        this.userMarker = null;
      }

      this.instructionsVisible = false;
      this.instructionList = [];

      [this.startCoords, this.endCoords] = [this.endCoords, this.startCoords];
      [this.startInput, this.endInput] = [this.endInput, this.startInput];

      if (this.startMarker && this.endMarker) {
        const s = this.startMarker.getLngLat();
        const e = this.endMarker.getLngLat();
        this.startMarker.setLngLat(e);
        this.endMarker.setLngLat(s);
      }
      if (this.startDotMarker && this.endDotMarker) {
        const s = this.startDotMarker.getLngLat();
        const e = this.endDotMarker.getLngLat();
        this.startDotMarker.setLngLat(e);
        this.endDotMarker.setLngLat(s);
      }

      this.lastDrawnStart = null;
      this.lastDrawnEnd = null;
      this.lastDrawnVia = null;

      if (this.startCoords && this.endCoords) {
        this.selectingPoint = null;
        this.tryDrawRoute();
      } else if (this.startCoords && !this.endCoords) {
        this.selectingPoint = "end";
      } else if (!this.startCoords && this.endCoords) {
        this.selectingPoint = "start";
      } else {
        this.selectingPoint = "start";
      }
    },
    // openFromPOI({ start, mode, startLabel }) {
    //   if (start?.lat && start?.lng) {
    //     this.startCoords = [start.lat, start.lng];

    //     if (startLabel) this.startInput = startLabel;
    //     else this.reverseGeocode('start', this.startCoords);
    //     this.selectingPoint = 'end';
    //     this.map?.flyTo({ center: [start.lng, start.lat], zoom: 16, speed: 0.8 });
    //   }
    //   if (mode) this.mode = mode === 'foot' ? 'walk' : mode;
    // },
    openFromPOI({end, mode, endLabel}) {
      if (end?.lat && end?.lng) {
        this.endCoords = [end.lat, end.lng];

        if (endLabel) this.endInput = endLabel;
        else this.reverseGeocode("end", this.endCoords);
        this.selectingPoint = "start";
        this.map?.flyTo({center: [end.lng, end.lat], zoom: 16, speed: 0.8});
      }
      if (mode) this.mode = mode === "foot" ? "walk" : mode;
    },
 drawStartMarker(labelText) {
  if (!this.map || !this.startCoords) return;

  if (this.startMarker) {
    this.startMarker.remove();
    this.startMarker = null;
  }

  if (this.startDotMarker) {
    this.startDotMarker.remove();
    this.startDotMarker = null;
  }

  const wrapper = document.createElement("div");
  wrapper.className = "marker-with-wrapper";

  const label = document.createElement("div");
  label.className = "pin-address-box";
  label.innerHTML = (labelText || this.startInput || "Se încarcă...")
    .split("\n")
    .map((line) => `<div>${line}</div>`)
    .join("");

  const pin = document.createElement("img");
  pin.src = require("@/assets/icons/green_pin.png");
  pin.style.width = "29px";
  pin.style.height = "45px";
  // pin.style.display = "block";

  wrapper.appendChild(label);
  wrapper.appendChild(pin);

  this.startMarker = new maplibregl.Marker({
    element: wrapper,
    //  anchor: "bottom-left",
    anchor: "bottom",
    draggable: true,
  })
    .setLngLat([this.startCoords[1], this.startCoords[0]])
    .addTo(this.map);

  const dot = document.createElement("div");
  dot.style.width = "16px";
  dot.style.height = "16px";
  dot.style.borderRadius = "50%";
  dot.style.backgroundColor = "#008000";
  dot.style.border = "2px solid white";
  dot.style.boxShadow = "0 0 6px rgba(0,0,0,.4)";

  this.startDotMarker = new maplibregl.Marker({
    element: dot,
    anchor: "center",
  })
    .setLngLat([this.startCoords[1], this.startCoords[0]])
    .addTo(this.map);

  this.startMarker.on("dragend", () => {
    const ll = this.startMarker.getLngLat();
    this.startCoords = [ll.lat, ll.lng];

    // if (this.startDotMarker) {
    //   this.startDotMarker.setLngLat([ll.lng, ll.lat]);
    // }

    this.reverseGeocode("start", this.startCoords);
    if (this.endCoords) this.tryDrawRoute();
  });
},

    drawStopMarker() {
      if (!this.map || !this.endCoords) return;
      if (this.endMarker) this.endMarker.remove();
      if (this.endDotMarker) {
        this.endDotMarker.remove();
        this.endDotMarker = null;
      }

      const pin = document.createElement("img");
      pin.src = require("@/assets/icons/red_pin.png");
      pin.style.width = "29px";
      pin.style.height = "45px";

      this.endMarker = new maplibregl.Marker({
        element: pin,
        anchor: "bottom",
        draggable: true,
      })
        .setLngLat([this.endCoords[1], this.endCoords[0]])
        .addTo(this.map);

      const dot = document.createElement("div");
      dot.style.width = "16px";
      dot.style.height = "16px";
      dot.style.borderRadius = "50%";
      dot.style.backgroundColor = "#DA2127";
      dot.style.border = "2px solid white";
      dot.style.boxShadow = "0 0 6px rgba(0,0,0,.4)";

      this.endDotMarker = new maplibregl.Marker({element: dot, anchor: "center"})
        .setLngLat([this.endCoords[1], this.endCoords[0]])
        .addTo(this.map);

      // drag -> actualizează END
      this.endMarker.on("dragend", () => {
        const ll = this.endMarker.getLngLat();
        this.endCoords = [ll.lat, ll.lng];
        this.reverseGeocode("end", this.endCoords);
        if (this.startCoords) this.tryDrawRoute();
      });
    },
    toggle2DView() {
      if (!this.map) return;

      if (this.is3DView) {
        this.map.setPitch(0);
        this.map.setBearing(0);
      } else {
        this.map.setPitch(85);
      }
      this.is3DView = !this.is3DView;
    },
    closeRouting() {
      this.$root.$emit("routingClosed");
      this.$emit("close");
      this.$root.$emit("openCategoriesMenu", {withOverlay: false});
    },

    initMap(lat, lng, zoom) {
      this.map = new maplibregl.Map({
        container: this.$refs.mapRouting,
        style:
          "https://static.maptoolkit.net/styles/toursprung/terrain.json?api_key=93563e3c-84df-435d-a44e-7a6c24e0d817",
        center: [lng, lat],
        zoom: zoom,
        minZoom: 3,
        maxZoom: 19,
        pitch: 0,
        bearing: 0,
      });

      this.map.on("load", () => {
        console.log("Map Routing loaded");
        const style = this.map.getStyle();
        style.layers.forEach((layer) => {
          if (layer.type === "symbol" && layer.id.toLowerCase().includes("poi")) {
            this.map.setLayoutProperty(layer.id, "visibility", "none");
          }
        });

        const nav = new maplibregl.NavigationControl({
          showCompass: true,
          showZoom: false,
        });
        this.map.addControl(nav);
        const ctrl = this.$refs.mapNavControl;
        if (ctrl) {
          const navControlEl = document.querySelector(".maplibregl-ctrl");
          if (navControlEl) {
            ctrl.appendChild(navControlEl);
          }
        }
      });
      this.map.on("click", (e) => {
        const hit = this.map.getLayer("route-hit")
          ? this.map.queryRenderedFeatures(e.point, {layers: ["route-hit"]})
          : [];
        if (hit && hit.length) return;
        const coords = [e.lngLat.lat, e.lngLat.lng];
        if (this.selectingPoint === "start") {
          this.startCoords = coords;
          this.reverseGeocode("start", coords);
          this.drawStartMarker(); 
          this.selectingPoint = "end";
          if (this.endCoords) {
      this.tryDrawRoute();
    }
        } else {
          this.endCoords = coords;
          this.reverseGeocode("end", coords);
          this.drawStopMarker();
          this.selectingPoint = null;
        }
        if (this.startCoords) {
      this.tryDrawRoute();
    }
        // this.tryDrawRoute();
      });
    },
    addViaPoint([lat, lng]) {
      const idx = this.viaPoints.length;
      this.viaPoints.push([lat, lng]);
      this.createViaMarker(idx, [lng, lat]);
      this.tryDrawRoute();
    },
    createViaMarker(index, lngLatArr) {
      const el = document.createElement("div");
      el.style.width = "18px";
      el.style.height = "18px";
      el.style.borderRadius = "50%";
      el.style.background = "#2a3561";
      el.style.border = "2px solid #fff";
      el.style.boxShadow = "0 0 6px rgba(0,0,0,.4)";
      el.style.cursor = "grab";
      el.style.zIndex = "9999";
      el.style.pointerEvents = "auto";
      el.style.touchAction = "none";

      const marker = new maplibregl.Marker({element: el, anchor: "center", draggable: true})
        .setLngLat(lngLatArr)
        .addTo(this.map);
      const stopPan = (e) => {
        e.stopPropagation?.();
        this.map.dragPan.disable();
      };
      const resumePan = () => this.map.dragPan.enable();
      el.addEventListener("pointerdown", stopPan);
      window.addEventListener("pointerup", resumePan, {passive: true});

      marker.on("dragend", () => {
        const ll = marker.getLngLat();
        const i = this.viaMarkers.indexOf(marker);
        if (i !== -1) this.viaPoints[i] = [ll.lat, ll.lng];
        this.tryDrawRoute();
        resumePan();
      });

      let downPt = null,
        moved = false;
      el.addEventListener("pointerdown", (e) => {
        downPt = {x: e.clientX, y: e.clientY};
        moved = false;
      });
      el.addEventListener("pointermove", (e) => {
        if (!downPt) return;
        if (Math.abs(e.clientX - downPt.x) > 5 || Math.abs(e.clientY - downPt.y) > 5) moved = true;
      });
      el.addEventListener("pointerup", (e) => {
        if (!moved) {
          e.stopPropagation?.();
          this.removeViaPointByMarker(marker);
        }
        downPt = null;
        moved = false;
      });

      el.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        this.removeViaPointByMarker(marker);
      });

      this.viaMarkers.push(marker);
    },
    clearViaMarkers() {
      this.viaMarkers.forEach((m) => m.remove());
      this.viaMarkers = [];
    },
    onInput(type, value) {
      if (!this.autocompleteService || !value) return;
      this.autocompleteService.getPlacePredictions({input: value}, (predictions, status) => {
        if (status !== "OK") return;
        if (type === "start") this.startSuggestions = predictions;
        if (type === "end") this.endSuggestions = predictions;
      });
    },
    selectSuggestion(type, suggestion) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({placeId: suggestion.place_id}, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          const coords = [loc.lat(), loc.lng()];
          if (type === "start") {
            this.startInput = suggestion.description;
            this.startCoords = coords;
            this.startSuggestions = [];
          } else {
            this.endInput = suggestion.description;
            this.endCoords = coords;
            this.endSuggestions = [];
          }
          this.tryDrawRoute();
        }
      });
    },
    reverseGeocode(type, coords) {
  if (!window.google || !google.maps) {
    console.warn("Google Maps API not available");
    return;
  }

  if (!this.geocoder) {
    this.geocoder = new google.maps.Geocoder();
  }

  const latlng = { lat: coords[0], lng: coords[1] };

  this.geocoder.geocode({ location: latlng }, (results, status) => {
    console.log("Reverse geocode status:", status, results);

    if (status === "OK" && results && results.length) {
      const result = results[0];
      const components = result.address_components || [];

      const street = components.find((c) => c.types.includes("route"))?.long_name || "";
      const streetNumber = components.find((c) => c.types.includes("street_number"))?.long_name || "";
      const locality =
        components.find((c) => c.types.includes("locality"))?.long_name ||
        components.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
        "";
      const country = components.find((c) => c.types.includes("country"))?.long_name || "";

      const firstLine = [street, streetNumber].filter(Boolean).join(" ").trim();

      const fallbackLine =
        result.formatted_address ||
        [locality, country].filter(Boolean).join(", ") ||
        `${coords[0].toFixed(5)}, ${coords[1].toFixed(5)}`;

      const finalLabel = firstLine || fallbackLine;

      if (type === "start") {
        this.startInput = finalLabel;
        this.drawStartMarker(finalLabel);
      }

      if (type === "end") {
        this.endInput = finalLabel;
        this.drawStopMarker(finalLabel);
      }
    } else {
      console.warn("Reverse geocoding failed:", status);

      const fallback = `${coords[0].toFixed(5)}, ${coords[1].toFixed(5)}`;

      if (type === "start") {
        this.startInput = fallback;
        this.drawStartMarker(fallback);
      }

      if (type === "end") {
        this.endInput = fallback;
        this.drawStopMarker(fallback);
      }
    }
  });
},
    reverseGeocodec(type, coords) {
      const latlng = {lat: coords[0], lng: coords[1]};

      this.geocoder.geocode({location: latlng}, (results, status) => {
        if (status === "OK" && results.length) {
          const validResult = results.find((r) => {
            return r.address_components.some((c) =>
              ["route", "locality", "administrative_area_level_1", "country"].some((type) => c.types.includes(type))
            );
          });

          if (validResult) {
            const components = validResult.address_components;

            const street = components.find((c) => c.types.includes("route"))?.long_name || "";
            const locality = components.find((c) => c.types.includes("locality"))?.long_name || "";
            const country = components.find((c) => c.types.includes("country"))?.long_name || "";

            const formatted = [street, locality, country].filter(Boolean).join(", ");

            if (type === "start") this.startInput = formatted;
            if (type === "end") this.endInput = formatted;
          } else {
            console.warn("A complete address with street/city/country was not found.");
          }
        } else {
          console.warn("Reverse geocoding failed:", status);
        }
      });
    },
    setMode(newMode) {
      const apiMode = newMode === "walk" ? "foot" : newMode;
      this.mode = newMode;

      if (this.startCoords && this.endCoords) {
        console.log("Recalculating route for mode:", apiMode);
        this.tryDrawRoute();
      } else {
        console.warn(" Missing coordinates for recalculations.");
      }
    },
    updateRouteSummary(distance, duration) {
      this.distanceText = (distance / 1000).toFixed(2) + " km";
      const h = Math.floor(duration / 3600);
      const m = Math.round((duration % 3600) / 60);
      this.durationText = `${h > 0 ? h + "h " : ""}${m}min`;
    },
    coordsEqual(a, b) {
      return a && b && a.length === b.length && a.every((v, i) => v === b[i]);
    },
    viaEqual(a, b) {
      if (!a && !b) return true;
      if (!a || !b || a.length !== b.length) return false;
      return a.every((p, i) => this.coordsEqual(p, b[i]));
    },
    tryDrawRoute() {
      if (!this.map || !this.startCoords || !this.endCoords) return;

      if (
        this.coordsEqual(this.startCoords, this.lastDrawnStart) &&
        this.coordsEqual(this.endCoords, this.lastDrawnEnd) &&
        this.viaEqual(this.viaPoints, this.lastDrawnVia) &&
        this.mode === this.lastDrawnMode
      )
        return;

      this.lastDrawnStart = [...this.startCoords];
      this.lastDrawnEnd = [...this.endCoords];
      this.lastDrawnVia = this.viaPoints.map((p) => [...p]);
      this.lastDrawnMode = this.mode;

      this.fetchRoute();
    },
    async fetchRoute() {
      if (this.ghostViaMarker) {
        this.ghostViaMarker.remove();
        this.ghostViaMarker = null;
      }
      if (!this.map || !this.startCoords || !this.endCoords) return;
      const url = new URL("https://routing.maptoolkit.net/route");
      const pts = [this.startCoords, ...this.viaPoints, this.endCoords];
      pts.forEach(([lat, lng]) => url.searchParams.append("point", `${lat},${lng}`));
      const apiMode = this.mode === "walk" ? "foot" : this.mode;
      url.searchParams.append("routeType", apiMode);
      url.searchParams.append("points_encoded", "false");
      url.searchParams.append("language", this.$i18n?.locale || "en");
      url.searchParams.append("api_key", "93563e3c-84df-435d-a44e-7a6c24e0d817");

      this.loading = true;
      try {
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const path = data?.paths?.[0];
        if (!path) return;

        const coords = path.points.coordinates;
        this.routeCoords = coords; // <== IMP snapToRoute
        this.routeSegmentLengths = [];
        this.routeTotalLength = 0;

        for (let i = 1; i < coords.length; i++) {
          const d = this._distMeters(coords[i - 1], coords[i]);
          this.routeSegmentLengths.push(d);
          this.routeTotalLength += d;
        }
        const featureAll = {
          type: "Feature",
          properties: {},
          geometry: {type: "LineString", coordinates: coords},
        };

        // --- cleanup
        const LAYERS = ["route-hit", "route-main", "route"];
        LAYERS.forEach((id) => {
          if (this.map.getLayer(id)) this.map.removeLayer(id);
        });
        ["route-all", "route-main", "route"].forEach((id) => {
          if (this.map.getSource(id)) this.map.removeSource(id);
        });

        this.map.addSource("route-all", {type: "geojson", data: featureAll});
        this.map.addSource("route-main", {type: "geojson", data: featureAll});
        this.map.addSource("route", {type: "geojson", data: featureAll});

        const getFirstTextSymbolId = () => {
          const layers = this.map.getStyle().layers || [];
          const l = layers.find((x) => x.type === "symbol" && x.layout && x.layout["text-field"]);
          return l && l.id;
        };
        const below = getFirstTextSymbolId();

        this.map.addLayer(
          {
            id: "route-hit",
            type: "line",
            source: "route-all",
            layout: {"line-cap": "round", "line-join": "round"},
            paint: {
              "line-color": "#000",
              "line-opacity": 0.001,
              "line-width": 30,
            },
          },
          below
        );

        const LINE_COLOR = "#a8a8a8"; // GREY
        const LINE_BORDER = "#2a3561"; //RED
        const LINE_OPACITY = 1;

        this.map.addLayer(
          {
            id: "route-main",
            type: "line",
            source: "route-main",
            layout: {"line-cap": "round", "line-join": "round"},
            paint: {
              "line-color": LINE_BORDER,
              "line-width": 8,
              "line-opacity": LINE_OPACITY,
            },
          },
          below
        );

        this.map.addLayer(
          {
            id: "route",
            type: "line",
            source: "route",
            layout: {"line-cap": "round", "line-join": "round"},
            paint: {
              "line-color": LINE_COLOR,
              "line-width": 5,
              "line-opacity": LINE_OPACITY,
            },
          },
          below
        );
        if (this._enterRoute) this.map.off("mouseenter", "route-hit", this._enterRoute);
        if (this._leaveRoute) this.map.off("mouseleave", "route-hit", this._leaveRoute);
        if (this._routeDown) {
          this.map.off("mousedown", "route-hit", this._routeDown);
          this.map.off("touchstart", "route-hit", this._routeDown);
        }
        this._enterRoute = () => {
          this.map.getCanvas().style.cursor = "pointer";
        };
        this._leaveRoute = () => {
          if (!this.draggingRoute) this.map.getCanvas().style.cursor = "";
        };
        this._routeDown = (e) => this.onRouteDown(e);

        this.map.on("mouseenter", "route-hit", this._enterRoute);
        this.map.on("mouseleave", "route-hit", this._leaveRoute);
        this.map.on("mousedown", "route-hit", this._routeDown);
        this.map.on("touchstart", "route-hit", this._routeDown);

        this.fitRouteBounds(coords);
        // this.addEndMarker(coords);
        this.addStartEndMarkers(coords);
        this.clearInstructionMarkers();

        this.addInstructionMarkers(path.instructions || []);
        this.instructionList = (path.instructions || []).map((i) => ({
          text: i.text,
          distance: Math.round(i.distance),
          coordinate: i.coordinate,
        }));
        this.updateRouteSummary(path.distance, path.time / 1000);

        this.reRenderViaHandles?.();
      } catch (err) {
        console.error("fetchRoute error:", err);
      } finally {
        this.loading = false;
      }
    },
    addStartEndMarkers(routeCoords) {
      document.querySelectorAll(".mtk-end-pin").forEach((n) => n.remove());
      if (this.startMarker) this.startMarker.remove();
      if (this.endMarker) this.endMarker.remove();
      if (this.startDotMarker) this.startDotMarker.remove();
      if (this.endDotMarker) this.endDotMarker.remove();

      const toLngLat = (c) => [c[0], c[1]];

      const startPin = document.createElement("img");
      startPin.src = require("@/assets/icons/green_pin.png");
      startPin.style.width = "29px";
      startPin.style.height = "45px";

      this.startMarker = new maplibregl.Marker({element: startPin, anchor: "bottom", draggable: true})
        .setLngLat(toLngLat(routeCoords[0]))
        .addTo(this.map);

      const startDot = document.createElement("div");
      startDot.style.width = "16px";
      startDot.style.height = "16px";
      startDot.style.borderRadius = "50%";
      startDot.style.backgroundColor = "#008000";
      startDot.style.border = "2px solid white";
      startDot.style.boxShadow = "0 0 6px rgba(0,0,0,0.4)";

      this.startDotMarker = new maplibregl.Marker({element: startDot, anchor: "center"})
        .setLngLat(toLngLat(routeCoords[0]))
        .addTo(this.map);

      this.startMarker.on("dragend", () => {
        const ll = this.startMarker.getLngLat();
        this.startCoords = [ll.lat, ll.lng];
        this.reverseGeocode("start", this.startCoords);
        this.tryDrawRoute();
      });

      const endPin = document.createElement("img");
      endPin.src = require("@/assets/icons/red_pin.png");
      endPin.style.width = "29px";
      endPin.style.height = "45px";
      endPin.className = "mtk-end-pin";

      this.endMarker = new maplibregl.Marker({element: endPin, anchor: "bottom", draggable: true})
        .setLngLat(toLngLat(routeCoords[routeCoords.length - 1]))
        .addTo(this.map);

      const endDot = document.createElement("div");
      endDot.style.width = "16px";
      endDot.style.height = "16px";
      endDot.style.borderRadius = "50%";
      endDot.style.backgroundColor = "#da2127";
      endDot.style.border = "2px solid white";
      endDot.style.boxShadow = "0 0 6px rgba(0,0,0,0.4)";

      this.endDotMarker = new maplibregl.Marker({element: endDot, anchor: "center"})
        .setLngLat(toLngLat(routeCoords[routeCoords.length - 1]))
        .addTo(this.map);

      this.endMarker.on("dragend", () => {
        const ll = this.endMarker.getLngLat();
        this.endCoords = [ll.lat, ll.lng];
        this.reverseGeocode("end", this.endCoords);
        this.tryDrawRoute();
      });
    },

    removeViaPointByMarker(marker) {
      const i = this.viaMarkers.indexOf(marker);
      if (i === -1) return;

      marker.remove();
      this.viaMarkers.splice(i, 1);
      this.viaPoints.splice(i, 1);
      this.tryDrawRoute();
    },
    removeViaPoint(index) {
      const m = this.viaMarkers[index];
      if (m) m.remove();
      this.viaMarkers.splice(index, 1);
      this.viaPoints.splice(index, 1);
      this.tryDrawRoute();
    },
    reRenderViaHandles() {
      this.clearViaMarkers();
      this.viaPoints.forEach(([lat, lng], i) => {
        this.createViaMarker(i, [lng, lat]);
      });
    },
    addRouteDragHandlers() {
      if (!this.map.getLayer("route")) return;

      this.map.on("mouseenter", "route", () => {
        this.map.getCanvas().style.cursor = "pointer";
      });

      this.map.on("mouseleave", "route", () => {
        if (!this.draggingRoute) this.map.getCanvas().style.cursor = "";
      });

      this.map.on("mousedown", "route", (e) => this.onRouteDown(e));
      this.map.on("touchstart", "route", (e) => this.onRouteDown(e));
    },
    onRouteDown(e) {
      e.preventDefault();
      this.draggingRoute = true;
      this.map.getCanvas().style.cursor = "grabbing";
      this.map.dragPan.disable();

      const {lng, lat} = e.lngLat;

      const el = document.createElement("div");
      el.style.width = "18px";
      el.style.height = "18px";
      el.style.borderRadius = "50%";
      el.style.background = "#5b57d9";
      el.style.border = "3px solid #ffffff";
      el.style.boxShadow = "0 0 6px rgba(0,0,0,.4)";
      el.style.pointerEvents = "none";

      this.ghostViaMarker = new maplibregl.Marker({element: el, anchor: "center"})
        .setLngLat([lng, lat])
        .addTo(this.map);

      this._onRouteMove = (ev) => this.onRouteMove(ev);
      this._onRouteUp = (ev) => this.onRouteUp(ev);

      this.map.on("mousemove", this._onRouteMove);
      this.map.on("touchmove", this._onRouteMove);
      this.map.on("mouseup", this._onRouteUp);
      this.map.on("touchend", this._onRouteUp);
    },
    onRouteMove(e) {
      if (!this.draggingRoute || !this.ghostViaMarker) return;
      const {lng, lat} = e.lngLat;
      this.ghostViaMarker.setLngLat([lng, lat]);
    },
    onRouteUp() {
      this.map.off("mousemove", this._onRouteMove);
      this.map.off("touchmove", this._onRouteMove);
      this.map.off("mouseup", this._onRouteUp);
      this.map.off("touchend", this._onRouteUp);

      this.map.dragPan.enable();
      this.map.getCanvas().style.cursor = "pointer";
      this.draggingRoute = false;

      if (this.ghostViaMarker) {
        const ll = this.ghostViaMarker.getLngLat();
        this.ghostViaMarker.remove();
        this.ghostViaMarker = null;

        this.addViaPoint([ll.lat, ll.lng]);
      }
    },
    _throttle(fn, wait = 100) {
      let ok = true;
      const call = (...args) => {
        if (!ok) return;
        ok = false;
        fn(...args);
        setTimeout(() => (ok = true), wait);
      };
      call.cancel = () => (ok = true);
      return call;
    },

    removeRouteLayer() {
      if (this.map.getLayer("route")) this.map.removeLayer("route");
      if (this.map.getSource("route")) this.map.removeSource("route");
    },

    clearMarkers() {
      this.instructionMarkers.forEach((m) => m.remove());
      this.instructionMarkers = [];
      if (this.endMarker) {
        this.endMarker.remove();
        this.endMarker = null;
      }
      if (this.startMarker) {
        this.startMarker.remove();
        this.startMarker = null;
      }
      if (this.startDotMarker) {
        this.startDotMarker.remove();
        this.startDotMarker = null;
      }
      if (this.endDotMarker) {
        this.endDotMarker.remove();
        this.endDotMarker = null;
      }

      document.querySelectorAll(".mtk-end-pin").forEach((n) => n.remove());
    },
    // fitRouteBounds(coords) {
    //   const bounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
    //   this.map.fitBounds(bounds, {padding: 80, duration: 1000, maxZoom: 17});
    // },
    fitRouteBounds(coords) {
  if (!this.map || !coords?.length) return;

  const bounds = coords.reduce(
    (b, c) => b.extend(c),
    new maplibregl.LngLatBounds(coords[0], coords[0])
  );

  const isMobile = window.innerWidth <= 599;

  if (isMobile) {
    const routingPanel = this.$el?.querySelector(".routing-ui");
    const panelHeight = routingPanel?.offsetHeight || 0;

    this.map.fitBounds(bounds, {
      padding: {
        top: 140,
        right: 40,
        left: 40,
        bottom: panelHeight + 40,
      },
      duration: 1000,
      maxZoom: 17,
    });
  } else {
    this.map.fitBounds(bounds, {
      padding: 80,
      duration: 1000,
      maxZoom: 17,
    });
  }
},
    // addInstructionMarkers(instructions) {
    //   instructions.forEach((instruction) => {
    //     const el = document.createElement("img");
    //     el.style.pointerEvents = "none";
    //     const m = new maplibregl.Marker({ element: el, anchor: "center" })
    //       .setLngLat([...instruction.coordinate].reverse())
    //       .addTo(this.map);
    //     this.instructionMarkers.push(m);
    //   });
    // },
    addInstructionMarkers(instructions) {
      instructions.forEach((instruction) => {
        const el = document.createElement("img");
        el.src = "https://static.maptoolkit.net/sprites/toursprung/route-via.svg";
        el.width = 12;
        el.height = 12;

        const marker = new maplibregl.Marker({element: el, anchor: "center"})
          .setLngLat([...instruction.coordinate].reverse())
          .setPopup(new maplibregl.Popup().setHTML(`<p>${instruction.text}</p>`))
          .addTo(this.map);

        this.instructionMarkers.push(marker);
      });
    },

    addEndMarker(coords) {
      const el = document.createElement("img");
      el.src = "https://static.maptoolkit.net/sprites/toursprung/marker.svg";
      el.width = 29;
      el.height = 30;
      this.endMarker = new maplibregl.Marker({element: el, anchor: "bottom"})
        .setLngLat(coords[coords.length - 1])
        .addTo(this.map);
    },
    useCurrentLocation(type) {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by the browser.");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = [position.coords.latitude, position.coords.longitude];
          const latlng = {lat: coords[0], lng: coords[1]};

          this.geocoder.geocode({location: latlng}, (results, status) => {
            if (status === "OK" && results[0]) {
              const components = results[0].address_components;

              const street = components.find((c) => c.types.includes("route"))?.long_name || "";
              const locality = components.find((c) => c.types.includes("locality"))?.long_name || "";
              const county = components.find((c) => c.types.includes("administrative_area_level_1"))?.long_name || "";
              const country = components.find((c) => c.types.includes("country"))?.long_name || "";

              const formatted = [street, locality, county, country].filter(Boolean).join(", ");

              if (type === "start") {
                this.startCoords = coords;
                this.startInput = formatted;
                this.selectingPoint = "end";
              } else {
                this.endCoords = coords;
                this.endInput = formatted;
                this.selectingPoint = null;
              }

              this.tryDrawRoute();
            } else {
              alert("Could not retrieve address from location.");
            }
          });
        },
        (error) => {
          alert("Location error: " + error.message);
        }
      );
    },

    toggleInstructions() {
      if (this.startCoords && this.endCoords) {
        this.instructionsVisible = !this.instructionsVisible;
      } else {
        alert(this.$t("routing.needBothPoints"));
      }
    },
    getIconForInstruction(text) {
      if (!text) return {icon: "navigation", text};

      const norm = (s) =>
        s
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");

      const t = norm(text);
      const current = this.$i18n.getLocaleMessage(this.$i18n.locale) || {};
      const en = this.$i18n.getLocaleMessage("en") || {};
      const dict = {...(current.instructions || {}), ...(en.instructions || {})};
      const keys = Object.keys(dict).sort((a, b) => b.length - a.length);

      for (const rawKey of keys) {
        if (t.includes(norm(rawKey))) {
          const val = dict[rawKey];
          return typeof val === "string" ? {icon: val, text} : val;
        }
      }
      return {icon: "navigation", text};
    },

    highlightInstructionPoint(instr) {
      if (!instr.coordinate || !this.map) return;
      if (this.highlightMarker) {
        this.highlightMarker.remove();
        this.highlightMarker = null;
      }
      const el = document.createElement("div");
      el.style.width = "22px";
      el.style.height = "22px";
      el.style.borderRadius = "50%";
      el.style.backgroundColor = "#da2127";
      el.style.border = "2px solid white";
      el.style.boxShadow = "0 0 6px rgba(0,0,0,0.4)";

      this.highlightMarker = new maplibregl.Marker({
        element: el,
        anchor: "center",
      })
        .setLngLat([...instr.coordinate].reverse())
        .addTo(this.map);

      this.map.flyTo({
        center: [...instr.coordinate].reverse(),
        zoom: 17,
        speed: 0.7,
      });
    },
    clearInstructionMarkers() {
      this.instructionMarkers.forEach((m) => m.remove());
      this.instructionMarkers = [];

      if (this.highlightMarker) {
        this.highlightMarker.remove();
        this.highlightMarker = null;
      }
    },
    // addStartEndMarkers(routeCoords) {
    //   if (this.startMarker) this.startMarker.remove();
    //   if (this.endMarker) this.endMarker.remove();
    //   if (this.startDotMarker) this.startDotMarker.remove();
    //   if (this.endDotMarker) this.endDotMarker.remove();

    //   const toLngLat = (coord) => [coord[0], coord[1]];

    //   // === START ===

    //   const startPin = document.createElement("img");
    //   startPin.src = require("@/assets/icons/green_pin.png");
    //   startPin.style.width = "29px";
    //   startPin.style.height = "45px";

    //   this.startMarker = new maplibregl.Marker({
    //     element: startPin,
    //     anchor: "bottom",
    //     draggable: true,
    //   })
    //     .setLngLat(toLngLat(routeCoords[0]))
    //     .addTo(this.map);

    //   const startDot = document.createElement("div");
    //   startDot.style.width = "16px";
    //   startDot.style.height = "16px";
    //   startDot.style.borderRadius = "50%";
    //   startDot.style.backgroundColor = "#008000";
    //   startDot.style.border = "2px solid white";
    //   startDot.style.boxShadow = "0 0 6px rgba(0,0,0,0.4)";

    //   this.startDotMarker = new maplibregl.Marker({
    //     element: startDot,
    //     anchor: "center",
    //   })
    //     .setLngLat(toLngLat(routeCoords[0]))
    //     .addTo(this.map);

    //   this.startMarker.on("dragend", () => {
    //     const lngLat = this.startMarker.getLngLat();
    //     this.startCoords = [lngLat.lat, lngLat.lng];
    //     this.reverseGeocode("start", this.startCoords);
    //     this.tryDrawRoute();
    //   });

    //   // === END ===

    //   const endPin = document.createElement("img");
    //   endPin.src = require("@/assets/icons/red_pin.png");
    //   endPin.style.width = "29px";
    //   endPin.style.height = "45px";

    //   this.endMarker = new maplibregl.Marker({
    //     element: endPin,
    //     anchor: "bottom",
    //     draggable: true,
    //   })
    //     .setLngLat(toLngLat(routeCoords[routeCoords.length - 1]))
    //     .addTo(this.map);

    //   const endDot = document.createElement("div");
    //   endDot.style.width = "16px";
    //   endDot.style.height = "16px";
    //   endDot.style.borderRadius = "50%";
    //   endDot.style.backgroundColor = "#da2127";
    //   endDot.style.border = "2px solid white";
    //   endDot.style.boxShadow = "0 0 6px rgba(0,0,0,0.4)";

    //   this.endDotMarker = new maplibregl.Marker({
    //     element: endDot,
    //     anchor: "center",
    //   })
    //     .setLngLat(toLngLat(routeCoords[routeCoords.length - 1]))
    //     .addTo(this.map);

    //   this.endMarker.on("dragend", () => {
    //     const lngLat = this.endMarker.getLngLat();
    //     this.endCoords = [lngLat.lat, lngLat.lng];
    //     this.reverseGeocode("end", this.endCoords);
    //     this.tryDrawRoute();
    //   });
    // },
    getFirstSymbolLayerId() {
      const layers = this.map.getStyle().layers;
      for (const layer of layers) {
        if (layer.type === "symbol" && layer.layout?.["text-field"]) {
          return layer.id;
        }
      }
      return null;
    },
  },
};
</script>

<style>
.marker-with-wrapper {
  position: relative;
  width: 34px;
  height: 46px;
  overflow: visible;
}

.marker-with-wrapper img {
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
}

.pin-address-box {
  position: absolute;
  left: 12px;
  bottom: 36px;
  background: #fff;
  color: #464646;
  font-family: Quicksand, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  padding: 4px 9px;
  border: 1px solid #a9a9a9;
  border-radius: 8px;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  z-index: 1;
   max-width: none;
  overflow: visible;
  text-overflow: unset;
}
.setting-buttons--routing {
  display: none !important;
}
.map-routing-overlay .maplibregl-ctrl-bottom-right {
  display: none;
}
.map-bottom-controls {
  position: absolute;
  bottom: 30px;
  right: 10px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}
.control-button {
  font-family: Quicksand, sans-serif;
  background-color: #ecf0f3;
  color: #444;
  font-weight: bold;
  font-size: 16px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #a9a9a9;
  box-shadow: 2px 2px 4px #9999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: background-color 0.2s, transform 0.2s;
}
.control-button:hover {
  background-color: #dadada;
}
.nav-control-wrapper .maplibregl-ctrl {
  background-color: #ecf0f3;
  border: 1px solid #a9a9a9;
  box-shadow: 2px 2px 4px #9999;
  border-radius: 12px;
  overflow: hidden;
  padding: 5px;
}
</style>

<style scoped>
/* .map-routing-overlay {
  position: relative;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1;
  pointer-events: auto;
} */
.map-routing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10020;
  pointer-events: auto;
}

.map-container {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  z-index: 1;
}

.loading-spinner {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 10px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  font-weight: bold;
  color: #333;
  z-index: 0;
}

.routing-ui {
  position: absolute;
  top: 20px;
  left: 10px;
  z-index: 10;
  width: 380px;
  background-color: #ecf0f3;
  border-radius: 18px;
  box-shadow: 2px 4px 4px #999999;
  padding: 18px;
  font-family: "Quicksand", sans-serif;
  border: 1px solid #a9a9a9;
  font-size: 15px;
  /* font-weight: 600; */
  color: #464646;
  margin-top: 110px;
}

@media (max-width: 599px) {
  .routing-ui {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;

    /* jos + safe-area pentru iOS */
    bottom: calc(env(safe-area-inset-bottom) + 23px) !important;

    width: calc(100% - 16px) !important;
    /* margin: 0 8px !important; */

    z-index: 10030 !important; /* peste hartă */
    padding: 12px;
    border-radius: 18px;

    /* să nu ocupe tot ecranul */
    max-height: calc(100vw - 24px);
    /* max-height: calc(100vh - 40px); */
    overflow: auto;

    /* optional: efect de "sheet" */
    /* box-shadow: 0 -8px 20px rgba(0,0,0,0.2); */
  }
  .routing-ui {
    position: fixed !important;
    left: 0;
    right: 0;
    bottom: calc(env(safe-area-inset-bottom) + 23px);
    max-height: calc(100vw - 24px);
    /* max-height: 55vh; */
    overflow: auto;
    z-index: 10030;
    margin-left: 8px !important;
  }

  /* butoane peste sheet */
  .map-bottom-controls {
    position: fixed !important;
    right: 10px;

    /* valoare dinamică */
    bottom: calc(var(--routing-height, 160px) + 23px);

    /* z-index: 10040; */
    transition: bottom 0.25s ease;
  }
}
/* 
@media (max-width: 599px) {
  .routing-ui {
   top: 110px;
    margin-left: 12px !important;
    left: 0px !important;
    width: 290px;
    margin: 0 auto;        
    padding: 12px;
    border-radius: 18px;
    z-index: 2;
    position: relative;    
    margin-top: 5px; 
  }
} */

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
}

.header-instructions {
  margin-bottom: 15px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.title {
  display: flex;
  align-items: center;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
}

.transport-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.transport-modes button {
  margin-right: 8px;
  padding: 10px;
  background: #f0f0f0;
  align-items: center;
  border-radius: 10px;
  border: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: background 0.2s ease;
}

.transport-modes button:hover {
  background: #d6d6d6;
}

.transport-modes .active {
  background-color: #d6d6d6;
}

.transport-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.transport-modes button {
  background: #f4f4f4;
  border: none;
  border-radius: 12px;
  width: 40px;
  height: 40px;
  font-size: 20px;
  margin-right: 6px;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: background 0.2s ease;
}

.transport-modes button:hover {
  background: #dcdcdc;
}

.transport-modes .active {
  background-color: #d0d0d0;
}

.transport-modes button span.material-icons-round {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  height: 100%;
  width: 100%;
}

.summary-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 14px;
  /* font-weight: 600; */
  color: #464646;
  line-height: 1.3em;
  white-space: nowrap;
  font-weight: 600;
}

.points .point {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
}

.input-wrapper {
  flex: 1;
  background: #f8f8f8;
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 8px 12px;
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: Quicksand, sans-serif;
  font-size: 14px;
  /* font-weight: 600; */
  color: #464646;
  line-height: 1.2em;
  letter-spacing: 0.03em;
}

.styled-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  font-family: Quicksand, sans-serif;
  color: #464646;
  line-height: 1.2em;
  letter-spacing: 0.03em;
}

.my-location-option {
  font-size: 14px;
  font-family: Quicksand, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.3em;
  color: #6c6a6a;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 5px;
  padding-top: 5px;
  padding-bottom: 8px;
}

.my-location-option:hover {
  background-color: #d6d6d6;
  border-radius: 6px;
}

.suggestions-list li {
  padding: 2.5px 5px;
  cursor: pointer;
  line-height: 1.6em;
  margin-left: 15px;
}

.suggestions-list li:not(:last-child) {
  border-bottom: 1px solid #e0e0e0;
}

.instructions-btn button {
  width: 100%;
  padding: 12px;
  background: #f0f0f0;
  border-radius: 10px;
  border: none;
  margin-top: 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  font-family: Quicksand, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #464646;
  line-height: 1.3em;
  letter-spacing: 0.03em;
  box-shadow: 2px 4px 4px #999999;
}

.instructions-btn button:hover {
  background: #dadada;
}

.instructions-panel ul {
  padding: 0;
  list-style: none;
}
.instructions-panel li {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4em;
}
.instructions-panel .icon {
  font-size: 16px;
  color: #464646;
}

.instructions-panel-v3 {
  max-height: 450px;
  overflow-y: auto;
  scrollbar-width: thin;
  padding-right: 10px;
  margin-top: 16px;
  background-color: white;
  border-radius: 18px;
  padding: 20px;
  font-family: Quicksand, sans-serif;
  border-color: #6c6a6a;
  border: 1px solid #a9a99a;
}

.mode-summary {
  text-align: center;
  margin-bottom: 12px;
  padding-right: 8px;
  padding-left: 8px;
}

.address-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
}

.address-icon {
  font-size: 18px;
}

.address-icon.red {
  color: #d73340;
}

.instruction-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.instruction-list li {
  border-bottom: 1px solid #a9a99a;
  padding: 5px 0;
  margin-bottom: 4px;
}

.step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.arrow-icon {
  width: 18px;
  height: 18px;
}

.instruction-text {
  font-size: 14px;
  color: #333;
}

.distance {
  font-size: 12px;
  color: #999;
  margin-left: 28px;
  margin-top: 4px;
}

.start-end-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin: 20px 0;
}

.pin-icon {
  width: 25px;
  height: 45px;
}

.car-icon {
  width: 28px;
  height: 28px;
  /* background: white; */
  padding: 2px;
  border-radius: 50%;
  z-index: 2;
}

.instructions-slide {
  background-color: #ecf0f3;
}

.mode-summary {
  text-align: center;
  margin-bottom: 12px;
}

.start-end-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin: 20px 0;
}

.pin-icon {
  width: 25px;
  height: 45px;
}

.line-container {
  flex: 1;
  position: relative;
  margin: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dotted-line {
  width: 100%;
  height: 0;
  border-top: 2px dotted #bbb;
  position: absolute;
  top: 50%;
  left: 0;
  z-index: 1;
}

.start-end-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0;
  position: relative;
}

.pin-icon {
  width: 25px;
  height: 45px;
}

.line-container {
  flex: 1;
  position: relative;
  margin: 0 10px;
  height: 40px;
}

.dotted-line {
  border-top: 2px dotted #999;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  z-index: 1;
}

.transport-bubble {
  margin-bottom: 18px;
}

.transport-icon {
  font-size: 25x;
  color: #464646;
  margin-bottom: 5px;
  padding-right: 5px;
}

.duration-text {
  font-size: 16px;
  font-weight: 600;
  color: #464646;
}

.instruction-icon {
  font-size: 25px;
  color: #464646;
}
.collapse-toggle-row {
  width: 100%;
  margin-top: 8px;
}

.collapse-toggle-btn {
  width: 100%;
  background: none;
  border: none;
  color: #464646;
  font-family: "Quicksand", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* padding: 10px 14px; */
  border-radius: 8px;
  transition: background 0.2s ease;
  text-align: left;
}

/* .collapse-toggle-btn:hover {
  background-color: #e0e0e0;
} */

.toggle-text {
  font-size: 16px;
  font-weight: 600;
  color: inherit;
  font-family: inherit;
}

.toggle-icon {
  font-size: 25px;
}

.points {
  position: relative;
}

.swap-fab {
  position: absolute;
  left: -5px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #f0f0f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 2;
}
.swap-fab:hover {
  background: #dadada;
}
.swap-fab .material-icons-round {
  font-size: 22px;
  color: #444;
}

@media (max-height: 770px), (max-width: 500px) {
  .routing-ui {
    max-height: calc(100vw - 24px);
    /* max-height: calc(100vh - 40px); */
  }

  .sidebar {
    max-height: 100%;
    overflow-y: auto;
    padding-right: 4px;
  }

  .sidebar .header,
  .sidebar .header-instructions {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #ecf0f3;
    padding-top: 4px;
    padding-bottom: 8px;
  }
}

.nav-arrow-marker {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.4);
  position: relative;
}

.nav-arrow-marker::after {
  content: "";
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 16px solid #1e88ff;
}

.nav-arrow-marker {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.4);
  position: relative;
}

.nav-arrow-marker::after {
  content: "";
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 16px solid #1e88ff;
}

.nav-arrow-marker--pulse::before {
  content: "";
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(30, 136, 255, 0.7);
  animation: navPulse 1.3s infinite;
}

@keyframes navPulse {
  0% {
    transform: scale(0.7);
    opacity: 0.8;
  }
  70% {
    transform: scale(1.4);
    opacity: 0;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}
.route-node {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffffff;
  border: 3px solid #2a3561;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
}
</style>


<style>
.nav-arrow-marker {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(220, 224, 230, 0.85);
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.nav-arrow-marker::after {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  background: #1b2a4a;
  border-radius: 50%;
  z-index: 1;
}

.nav-arrow-marker svg {
  width: 12px;
  height: 12px;
  z-index: 2;
}
.nav-arrow-marker--pulse {
  animation: navPulse 1.3s infinite;
}
@keyframes navPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(150, 150, 150, 0.6);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(150, 150, 150, 0);
  }
  100% {
    box-shadow: 0 0 0 12px rgba(150, 150, 150, 0);
  }
}
</style>




